import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { Column } from '@repo/ui/data-table'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import React from 'react'
import { toast } from 'sonner'

import { API } from '../../../../api/apis.config'
import { generateAdvanceCenterOlPayments } from '../../../../api/center-ol-payments/generate-advance-center-ol-payments'
import { generateCostCenterOlPayments } from '../../../../api/center-ol-payments/generate-cost-center-ol-payments'
import { getSuggestedAdvancePaymentAmount } from '../../../../api/center-ol-payments/get-suggested-advance-payment-amount'
import { getCenter } from '../../../../api/centers/get-center'
import { getOlPermissions } from '../../../../lib/constants/ol-permissions'
import useAuth from '../../../../lib/hooks/use-auth'
import { useDataProps } from '../../../../lib/hooks/use-data-props'
import useTranslate from '../../../../lib/hooks/use-translate'
import { CenterStatus } from '../../../../lib/types/enums/center/center-status'
import { getCenterOlPaymentStatus } from '../../../../lib/types/enums/center-ol-payment/center-ol-payment-status.enum'
import {
  CenterOlPaymentTypeEnum,
  centerOlPaymentTypeMapping
} from '../../../../lib/types/enums/center-ol-payment/center-ol-payment-type.enum'
import { OLTypeEnum } from '../../../../lib/types/enums/ol/ol-type.enum'
import { Center } from '../../../../lib/types/models/center/center'
import {
  CenterOlPayment,
  CenterOlPaymentFilters
} from '../../../../lib/types/models/center-ol-payment/center-ol-payment'
import { DataProps } from '../../../../lib/types/utils'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import DynamicDataTable, { DynamicTableRef } from '../../../elements/dynamic-table'
import { CenterCostsTableProps } from './utils'

type Props = Omit<CenterCostsTableProps, 'costType' | 'mode'> & {
  centerId: number
  olType?: OLTypeEnum
}

export function CenterOlPaymentsTable(props: DataProps<Center, Props>) {
  const { centerId, currency, olType } = props
  const { t } = useTranslate()
  const { user } = useAuth()
  const tableRef = React.useRef<DynamicTableRef<CenterOlPayment>>(null)
  const [payments, setPayments] = React.useState<CenterOlPayment[]>([])
  const { data: center } = useDataProps(props, (id) => getCenter(id, 'FULL'))

  const { data: suggestedAdvance } = useQuery({
    initialData: { amount: 0 },
    queryKey: ['suggested-advance-payment-amount', centerId],
    queryFn: () => getSuggestedAdvancePaymentAmount({ centerId })
  })

  const generateAdvancePaymentsMutation = useMutation({
    onError: showAxiosError,
    mutationKey: ['generate-advance-center-ol-payment'],
    mutationFn: generateAdvanceCenterOlPayments,
    onSuccess: () => {
      tableRef.current?.refetch()
      toast.success('Les virements ont été créés avec succès')
    }
  })

  const generateCostPaymentsMutation = useMutation({
    onError: showAxiosError,
    mutationKey: ['generate-cost-center-ol-payment'],
    mutationFn: generateCostCenterOlPayments,
    onSuccess: () => {
      tableRef.current?.refetch()
      toast.success('Les virements ont été créés avec succès')
    }
  })

  const olPermissions = getOlPermissions({ role: user.role, type: olType })

  const isPending =
    generateAdvancePaymentsMutation.isPending || generateCostPaymentsMutation.isPending

  const columns: Column<CenterOlPayment>[] = [
    {
      header: t('center.costs.ol.payments.column.date'),
      accessorKey: 'dateOfPayment',
      centerCell: true,
      cell: ({ row }) => <Badge text={DateFormatter.DDMMYYYY(row.original.dateOfPayment)} />
    },
    {
      header: t('center.costs.ol.payments.column.type'),
      accessorKey: 'type',
      centerCell: true,
      cell: ({ row }) => (
        <Badge
          variant={centerOlPaymentTypeMapping[row.original.type].variant}
          text={centerOlPaymentTypeMapping[row.original.type].label}
        />
      )
    },
    {
      header: t('center.costs.ol.payments.column.amount'),
      accessorKey: 'amount',
      centerCell: true,
      cell: ({ row }) => (
        <Badge
          variant="primaryLight"
          text={NumberFormatter.formatCurrency(row.original.amount, row.original.currency)}
        />
      )
    },
    {
      header: t('center.costs.ol.payments.column.status'),
      accessorKey: 'status',
      centerCell: true,
      cell: ({ row }) => {
        const { color, label } = getCenterOlPaymentStatus(row.original)
        return <Badge variant={color} text={label} />
      }
    }
  ]

  return (
    <div className={'flex flex-col gap-2'}>
      <div className={'flex flex-row justify-between items-center'}>
        <div className={'flex flex-row gap-3 items-center'}>
          <Typography variant={'body'} color={tailwindColors.primary.DEFAULT}>
            {t('center.costs.ol.payments.title')}
          </Typography>

          {olPermissions['create-center-costs-ol-payments'] && (
            <>
              <Button
                variant={'outline'}
                className="border-grey-light"
                label={t('center.costs.ol.payments.advanced.payment')}
                onClick={() => {
                  generateAdvancePaymentsMutation.mutate([centerId])
                }}
                loading={isPending}
                // disable the button if
                // - there is already an advance payment
                disabled={payments?.some(
                  (payment) => payment.type === CenterOlPaymentTypeEnum.ADVANCE
                )}
              />

              <Button
                variant={'outline'}
                className="border-grey-light"
                label={t('center.costs.ol.payments.balance.transfer')}
                onClick={() => {
                  generateCostPaymentsMutation.mutate([centerId])
                }}
                loading={isPending}
                // disable the button if
                // - the center costs is not validated
                // - there is no advance payment
                // - there is already a cost payment
                disabled={
                  center?.costsStatus !== CenterStatus.VALIDATED ||
                  !payments?.some((payment) => payment.type === CenterOlPaymentTypeEnum.ADVANCE) ||
                  payments?.some((payment) => payment.type === CenterOlPaymentTypeEnum.COST)
                }
              />
            </>
          )}
        </div>

        {olPermissions['create-center-costs-ol-payments'] && (
          <Badge
            variant="blue"
            text={`${t('center.costs.ol.payments.advance.amount')} ${NumberFormatter.formatCurrency(suggestedAdvance.amount, currency)}`}
          />
        )}
      </div>

      <div className={'bg-white rounded-3xl mt-2'}>
        <DynamicDataTable<CenterOlPayment, CenterOlPaymentFilters>
          ref={tableRef}
          onDataChange={setPayments}
          title="Tous les virements"
          deleteMessage="Etes-vous sûr de vouloir supprimer ce virement ?"
          columns={columns}
          headerType="none"
          allowSearchParam
          paginated={false}
          endpoint={API.centerOlPayments.getAll.url}
          filters={{ centerId }}
        />
      </div>
    </div>
  )
}
